import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardDocumentListIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ChartBarIcon,
  PlusIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  BellIcon,
  StarIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import {
  ClipboardDocumentListIcon as ClipboardDocumentListIconSolid,
  CalendarDaysIcon as CalendarDaysIconSolid,
} from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const TasksDashboard = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Task statistics
  const taskStats = {
    total: 24,
    inProgress: 8,
    pending: 12,
    completed: 4,
    overdue: 3,
    dueToday: 5,
  };

  // Recent tasks data
  const recentTasks = [
    {
      id: 1,
      title: "Complete Annual Health Assessment",
      assignedBy: "Dr. Meera Gupta",
      dueDate: "2025-07-25",
      priority: "High",
      status: "In Progress",
      description: "Annual mandatory health screening and assessment",
      project: "Healthcare Compliance",
      progress: 65,
      tags: ["Health", "Mandatory", "Annual"],
      estimatedHours: 4,
      actualHours: 2.5,
      assignee: "You",
      createdDate: "2025-07-20",
    },
    {
      id: 2,
      title: "Review New Medical Protocol Guidelines",
      assignedBy: "Medical Director",
      dueDate: "2025-07-30",
      priority: "Medium",
      status: "Pending",
      description: "Review and acknowledge updated treatment protocols",
      project: "Protocol Updates",
      progress: 0,
      tags: ["Protocol", "Review", "Medical"],
      estimatedHours: 2,
      actualHours: 0,
      assignee: "You",
      createdDate: "2025-07-22",
    },
    {
      id: 3,
      title: "Submit Monthly Quality Metrics",
      assignedBy: "Quality Assurance Team",
      dueDate: "2025-08-01",
      priority: "High",
      status: "Not Started",
      description: "Compile and submit departmental quality metrics",
      project: "Quality Management",
      progress: 0,
      tags: ["Quality", "Metrics", "Monthly"],
      estimatedHours: 3,
      actualHours: 0,
      assignee: "You",
      createdDate: "2025-07-23",
    },
    {
      id: 4,
      title: "Equipment Maintenance Checklist",
      assignedBy: "Facilities Manager",
      dueDate: "2025-07-26",
      priority: "Medium",
      status: "In Progress",
      description: "Complete weekly equipment maintenance checks",
      project: "Facilities Management",
      progress: 40,
      tags: ["Equipment", "Maintenance", "Weekly"],
      estimatedHours: 2.5,
      actualHours: 1,
      assignee: "You",
      createdDate: "2025-07-21",
    },
    {
      id: 5,
      title: "Patient Satisfaction Survey Analysis",
      assignedBy: "Quality Director",
      dueDate: "2025-07-28",
      priority: "Low",
      status: "Completed",
      description: "Analyze Q2 patient satisfaction survey results",
      project: "Patient Experience",
      progress: 100,
      tags: ["Survey", "Analysis", "Patient"],
      estimatedHours: 6,
      actualHours: 5.5,
      assignee: "You",
      createdDate: "2025-07-15",
    },
  ];

  // Project overview data
  const projects = [
    {
      id: 1,
      name: "Healthcare Compliance",
      tasksCount: 8,
      completedTasks: 3,
      progress: 37.5,
      dueDate: "2025-08-15",
      status: "On Track",
      teamMembers: 5,
    },
    {
      id: 2,
      name: "Protocol Updates",
      tasksCount: 12,
      completedTasks: 8,
      progress: 66.7,
      dueDate: "2025-08-30",
      status: "On Track",
      teamMembers: 8,
    },
    {
      id: 3,
      name: "Quality Management",
      tasksCount: 15,
      completedTasks: 12,
      progress: 80,
      dueDate: "2025-09-15",
      status: "Ahead",
      teamMembers: 6,
    },
    {
      id: 4,
      name: "Facilities Management",
      tasksCount: 6,
      completedTasks: 2,
      progress: 33.3,
      dueDate: "2025-07-28",
      status: "At Risk",
      teamMembers: 4,
    },
  ];

  // Quick actions
  const quickActions = [
    {
      title: "Create New Task",
      description: "Add a new task or assignment",
      icon: PlusIcon,
      color: "bg-blue-500",
      link: "/tasks/new",
    },
    {
      title: "View Calendar",
      description: "See all tasks in calendar view",
      icon: CalendarDaysIcon,
      color: "bg-green-500",
      link: "/tasks/calendar",
    },
    {
      title: "Team Tasks",
      description: "View team assignments",
      icon: UserGroupIcon,
      color: "bg-purple-500",
      link: "/tasks/team",
    },
    {
      title: "Analytics",
      description: "Task performance analytics",
      icon: ChartBarIcon,
      color: "bg-orange-500",
      link: "/tasks/analytics",
    },
  ];

  // Filter tasks based on selected filter and search query
  const filteredTasks = recentTasks.filter(task => {
    const matchesFilter = selectedFilter === 'all' || 
      task.status.toLowerCase().replace(' ', '') === selectedFilter;
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.project.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Helper functions
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'text-red-600 bg-red-50';
      case 'Medium': return 'text-yellow-600 bg-yellow-50';
      case 'Low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'text-green-600 bg-green-50';
      case 'In Progress': return 'text-blue-600 bg-blue-50';
      case 'Pending': return 'text-yellow-600 bg-yellow-50';
      case 'Not Started': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getProjectStatusColor = (status) => {
    switch (status) {
      case 'On Track': return 'text-green-600 bg-green-50';
      case 'Ahead': return 'text-blue-600 bg-blue-50';
      case 'At Risk': return 'text-red-600 bg-red-50';
      case 'Delayed': return 'text-orange-600 bg-orange-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date().setHours(0, 0, 0, 0);
  };

  const isDueToday = (dueDate) => {
    const today = new Date().toDateString();
    return new Date(dueDate).toDateString() === today;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 sm:py-6 space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Tasks & Projects</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                Manage your tasks, track progress, and collaborate with your team
              </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <div className="text-xs sm:text-sm text-gray-500">
                {currentTime.toLocaleTimeString()}
              </div>
              <Link
                to="/tasks/new"
                className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base"
              >
                <PlusIcon className="h-4 w-4" />
                <span>New Task</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6 border">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClipboardDocumentListIconSolid className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Total Tasks</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{taskStats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6 border">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{taskStats.inProgress}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6 border">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-orange-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Pending</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{taskStats.pending}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6 border">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Completed</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{taskStats.completed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6 border">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BellIcon className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Overdue</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{taskStats.overdue}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6 border">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarDaysIconSolid className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Due Today</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{taskStats.dueToday}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Main Tasks Section */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 sm:p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
              </div>
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  {quickActions.map((action, index) => (
                    <Link
                      key={index}
                      to={action.link}
                      className="flex flex-col items-center p-3 sm:p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all group"
                    >
                      <div className={`${action.color} p-2 sm:p-3 rounded-lg mb-2 sm:mb-3 group-hover:scale-110 transition-transform`}>
                        <action.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                      <h3 className="font-medium text-gray-900 text-center mb-1 text-xs sm:text-sm">{action.title}</h3>
                      <p className="text-xs text-gray-500 text-center">{action.description}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Tasks List */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 sm:p-6 border-b">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                  <h2 className="text-lg font-semibold text-gray-900">My Tasks</h2>
                  
                  {/* Search and Filter */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                    <div className="relative">
                      <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search tasks..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-64"
                      />
                    </div>
                    
                    <div className="relative">
                      <select
                        value={selectedFilter}
                        onChange={(e) => setSelectedFilter(e.target.value)}
                        className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-auto"
                      >
                        <option value="all">All Tasks</option>
                        <option value="inprogress">In Progress</option>
                        <option value="pending">Pending</option>
                        <option value="notstarted">Not Started</option>
                        <option value="completed">Completed</option>
                      </select>
                      <FunnelIcon className="h-4 w-4 absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="divide-y divide-gray-200">
                {filteredTasks.length === 0 ? (
                  <div className="p-6 sm:p-8 text-center">
                    <ClipboardDocumentListIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No tasks found matching your criteria.</p>
                  </div>
                ) : (
                  filteredTasks.map((task) => (
                    <div key={task.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0">
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-2">
                            <Link
                              to={`/tasks/${task.id}`}
                              className="text-base sm:text-lg font-medium text-gray-900 hover:text-blue-600 transition-colors"
                            >
                              {task.title}
                            </Link>
                            <div className="flex flex-wrap gap-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(task.priority)}`}>
                                {task.priority}
                              </span>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                                {task.status}
                              </span>
                              {isOverdue(task.dueDate) && (
                                <span className="px-2 py-1 text-xs font-medium rounded-full text-red-600 bg-red-50">
                                  Overdue
                                </span>
                              )}
                              {isDueToday(task.dueDate) && (
                                <span className="px-2 py-1 text-xs font-medium rounded-full text-orange-600 bg-orange-50">
                                  Due Today
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <p className="text-sm sm:text-base text-gray-600 mb-3">{task.description}</p>
                          
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-6 text-xs sm:text-sm text-gray-500">
                            <div className="flex items-center">
                              <UserGroupIcon className="h-4 w-4 mr-1" />
                              <span>{task.assignedBy}</span>
                            </div>
                            <div className="flex items-center">
                              <CalendarDaysIcon className="h-4 w-4 mr-1" />
                              <span>Due {formatDate(task.dueDate)}</span>
                            </div>
                            <div className="flex items-center">
                              <ClockIcon className="h-4 w-4 mr-1" />
                              <span>{task.actualHours}h / {task.estimatedHours}h</span>
                            </div>
                          </div>
                          
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 mt-4">
                            <div className="flex items-center space-x-2">
                              <span className="text-xs sm:text-sm text-gray-500">Project:</span>
                              <Link
                                to={`/projects/${task.project.toLowerCase().replace(' ', '-')}`}
                                className="text-xs sm:text-sm text-blue-600 hover:text-blue-700"
                              >
                                {task.project}
                              </Link>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <div className="flex flex-wrap gap-1">
                                {task.tags.slice(0, 3).map((tag, index) => (
                                  <span
                                    key={index}
                                    className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                          
                          {/* Progress Bar */}
                          {task.progress > 0 && (
                            <div className="mt-4">
                              <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 mb-1">
                                <span>Progress</span>
                                <span>{task.progress}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${task.progress}%` }}
                                ></div>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="ml-0 mt-3 sm:ml-4 sm:mt-0 flex justify-end">
                          <Link
                            to={`/tasks/${task.id}`}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {filteredTasks.length > 0 && (
                <div className="p-4 sm:p-6 border-t bg-gray-50">
                  <Link
                    to="/tasks/all"
                    className="flex items-center justify-center text-blue-600 hover:text-blue-700 font-medium"
                  >
                    View All Tasks
                    <ArrowRightIcon className="h-4 w-4 ml-2" />
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Project Overview */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 sm:p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Active Projects</h2>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                {projects.map((project) => (
                  <div key={project.id} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-2 sm:space-y-0 mb-3">
                      <div>
                        <Link
                          to={`/projects/${project.id}`}
                          className="font-medium text-gray-900 hover:text-blue-600 transition-colors"
                        >
                          {project.name}
                        </Link>
                        <p className="text-xs sm:text-sm text-gray-500 mt-1">
                          {project.completedTasks} of {project.tasksCount} tasks completed
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full self-start ${getProjectStatusColor(project.status)}`}>
                        {project.status}
                      </span>
                    </div>
                    
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 mb-1">
                        <span>Progress</span>
                        <span>{project.progress.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-1 sm:space-y-0 text-xs sm:text-sm text-gray-500">
                      <div className="flex items-center">
                        <UserGroupIcon className="h-4 w-4 mr-1" />
                        <span>{project.teamMembers} members</span>
                      </div>
                      <div className="flex items-center">
                        <CalendarDaysIcon className="h-4 w-4 mr-1" />
                        <span>Due {formatDate(project.dueDate)}</span>
                      </div>
                    </div>
                  </div>
                ))}
                
                <Link
                  to="/projects"
                  className="block text-center text-blue-600 hover:text-blue-700 font-medium py-2"
                >
                  View All Projects →
                </Link>
              </div>
            </div>

            {/* Upcoming Deadlines */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 sm:p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Upcoming Deadlines</h2>
              </div>
              <div className="p-4 sm:p-6">
                <div className="space-y-3">
                  {recentTasks
                    .filter(task => task.status !== 'Completed')
                    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
                    .slice(0, 5)
                    .map((task) => (
                      <div key={task.id} className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/tasks/${task.id}`}
                            className="text-xs sm:text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors truncate block"
                          >
                            {task.title}
                          </Link>
                          <p className="text-xs text-gray-500">{task.project}</p>
                        </div>
                        <div className="ml-3 text-right">
                          <p className={`text-xs font-medium ${
                            isOverdue(task.dueDate) ? 'text-red-600' :
                            isDueToday(task.dueDate) ? 'text-orange-600' :
                            'text-gray-600'
                          }`}>
                            {isOverdue(task.dueDate) ? 'Overdue' :
                             isDueToday(task.dueDate) ? 'Today' :
                             formatDate(task.dueDate)
                            }
                          </p>
                          <span className={`px-1 py-0.5 text-xs rounded-full ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            {/* Performance Summary */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900">This Week</h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Tasks Completed</span>
                    <div className="flex items-center">
                      <StarIcon className="h-4 w-4 text-yellow-500 mr-1" />
                      <span className="font-medium">8</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Hours Logged</span>
                    <span className="font-medium">32.5h</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">On-Time Delivery</span>
                    <span className="font-medium text-green-600">95%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Projects Active</span>
                    <span className="font-medium">4</span>
                  </div>
                </div>
                
                <Link
                  to="/tasks/analytics"
                  className="block text-center text-blue-600 hover:text-blue-700 font-medium py-2 mt-4 border-t pt-4"
                >
                  View Analytics →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TasksDashboard;
